import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Clipboard, Check, X, MessageCircle } from "lucide-react"
import { useState } from "react"

interface UploadedFile {
  name: string;
  url: string;
}

interface PromptPreviewProps {
  prompt: string;
  uploadedFiles: UploadedFile[];
  onProceed: () => void;
  onUpdatePrompt: (newPrompt: string) => void;
  onRemoveFile: (file: UploadedFile) => void;
}

export const PromptPreview = ({ 
  prompt, 
  uploadedFiles, 
  onProceed, 
  onUpdatePrompt,
  onRemoveFile 
}: PromptPreviewProps) => {
  const [copied, setCopied] = useState(false)
  const [editedPrompt, setEditedPrompt] = useState(prompt)

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(editedPrompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedPrompt(e.target.value)
    onUpdatePrompt(e.target.value)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <Textarea
          value={editedPrompt}
          onChange={handlePromptChange}
          className="min-h-[200px] font-mono text-sm"
        />
        {uploadedFiles.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {uploadedFiles.map((file, index) => (
              file.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                <div key={index} className="relative group">
                  <img 
                    src={file.url} 
                    alt={file.name}
                    className="w-full aspect-square object-cover rounded-md"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => onRemoveFile(file)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 bg-background py-4">
        <Button
          variant="outline"
          className="w-full"
          onClick={handleCopyToClipboard}
        >
          {copied ? (
            <>
              <Check className="mr-2 h-4 w-4" />
              Copied!
            </>
          ) : (
            <>
              <Clipboard className="mr-2 h-4 w-4" />
              Copy to Clipboard
            </>
          )}
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button 
            onClick={onProceed}
            className="bg-[#7C3AED] hover:bg-[#7C3AED]/90"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Try with Claude
          </Button>
          <Button 
            onClick={onProceed}
            className="bg-[#10A37F] hover:bg-[#10A37F]/90"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Try with ChatGPT
          </Button>
          <Button 
            onClick={onProceed}
            className="bg-[#1A73E8] hover:bg-[#1A73E8]/90"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Try with Gemini
          </Button>
          <Button 
            onClick={onProceed}
            className="bg-[#0066FF] hover:bg-[#0066FF]/90"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Try with Deepseek
          </Button>
        </div>
      </div>
    </div>
  )
}